import { stateOf } from './state.js';
import { ORDER, sortByKey } from './ordering.js';
/**
 * 各画面のリスト。確定済みHTMLの filter 条件をそのまま移したもので、
 * 画面からもAPIからも同じ関数を使い、表示と集計がずれないようにする。
 */
export const selectors = {
    clerkTodo: (all) => sortByKey(all.filter((c) => !c.clerk), ORDER.todo),
    clerkDone: (all) => sortByKey(all.filter((c) => c.clerk), ORDER.done),
    /**
     * 薬剤部の調製前。中止は薬剤部が確認するまで残す。
     * クラークがまだ入力していない患者も、末尾に「入力前」として出す（この後に来る件数を見通すため）。
     */
    rxWait: (all) => sortByKey(all.filter((c) => !c.pharm && !(c.clerk?.type === 'stop' && c.stopAck)), ORDER.rxWait),
    /** 薬剤部の調製開始。調製完了を入れた案件は rxDone へ移る。 */
    rxStarted: (all) => sortByKey(all.filter((c) => c.pharm && !c.pharmDone), ORDER.rxStarted),
    rxDone: (all) => sortByKey(all.filter((c) => c.pharm && c.pharmDone), ORDER.rxDone),
    rxStop: (all) => sortByKey(all.filter((c) => c.clerk?.type === 'stop' && c.stopAck), ORDER.rxStop),
    nurseWait: (all) => sortByKey(all.filter((c) => !c.admin && c.clerk?.type !== 'stop'), ORDER.nWait),
    /** 投与中。投与完了を入れた案件は nurseDone へ移る。 */
    nurseAdmin: (all) => sortByKey(all.filter((c) => c.admin && !c.adminDone), ORDER.nAdmin),
    nurseDone: (all) => sortByKey(all.filter((c) => c.admin && c.adminDone), ORDER.nDone),
    nurseStop: (all) => sortByKey(all.filter((c) => c.clerk?.type === 'stop'), ORDER.nStop),
};
/**
 * 大型モニタの並び。いちばん見たい「調製待ち → 調製開始（調製完了を含む）→ 投与中」を上に、
 * まだ何も始まっていない入力前の患者をその下に、中止は末尾にまとめる。
 * 薬剤部が確認した中止は調製前と同じく落とす。投与完了まで済んだ案件は出さない。
 */
export function monitorModel(all, now, max = 16) {
    const isLive = (c) => stateOf(c) !== 'stop';
    const stopped = sortByKey(all.filter((c) => stateOf(c) === 'stop' && !c.stopAck), ORDER.monStopped);
    const wait = selectors.rxWait(all).filter((c) => c.clerk && isLive(c));
    const making = sortByKey(all.filter((c) => c.pharm && !c.admin && isLive(c)), ORDER.monMaking);
    const giving = sortByKey(all.filter((c) => c.admin && !c.adminDone && isLive(c)), ORDER.monGiving);
    const idle = sortByKey(all.filter((c) => !c.clerk), ORDER.todo);
    const oldestWaitMin = wait
        .filter((c) => c.clerk?.type === 'confirm')
        .reduce((m, c) => Math.max(m, Math.floor(Math.max(0, now - (c.clerk?.at ?? now)) / 60000)), 0);
    const active = [
        ...wait.map((c) => ({ case: c, at: c.clerk?.at ?? now, kind: 'wait' })),
        // 調製完了まで済んだ行は「調製完了から◯分」として出す（投与開始までの待ちが見える）
        ...making.map((c) => c.pharmDone
            ? { case: c, at: c.pharmDone.at, kind: 'made' }
            : { case: c, at: c.pharm?.at ?? now, kind: 'start' }),
        ...giving.map((c) => ({ case: c, at: c.admin?.at ?? now, kind: 'admin' })),
        ...idle.map((c) => ({ case: c, at: null, kind: 'idle' })),
    ];
    const stops = stopped.map((c) => ({
        case: c,
        at: c.clerk?.at ?? now,
        kind: 'stop',
    }));
    const rows = [...active, ...stops];
    return {
        // 未確認の中止は必ず出す。行が足りないときは、進行中の行のほうを削る
        rows: [...active.slice(0, Math.max(0, max - stops.length)), ...stops].slice(0, max),
        waitCount: wait.length,
        makingCount: making.length,
        givingCount: giving.length,
        stoppedCount: stopped.length,
        idleCount: idle.length,
        oldestWaitMin,
        overflow: Math.max(0, rows.length - max),
    };
}
//# sourceMappingURL=lists.js.map